import { useState, useCallback, useMemo } from 'react';
import { AdminUser, UserFilters, AdminPagination, AdminUserDetail, AdminUserActivity, AdminUserHome, AdminUserRelationships } from '../../types/admin';

// ============================================================================
// MOCK DATA GENERATION
// ============================================================================

const FIRST_NAMES = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Barbara', 'David', 'Elizabeth', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Christopher', 'Karen', 'Daniel', 'Nancy', 'Matthew', 'Lisa',
  'Anthony', 'Betty', 'Mark', 'Margaret', 'Donald', 'Sandra', 'Steven', 'Ashley',
  'Paul', 'Emily', 'Andrew', 'Kimberly', 'Joshua', 'Donna', 'Kenneth', 'Michelle',
  'Kevin', 'Carol', 'Brian', 'Amanda', 'George', 'Melissa', 'Timothy', 'Deborah',
  'Ronald', 'Stephanie', 'Edward', 'Rebecca', 'Jason', 'Sharon', 'Jeffrey', 'Laura',
];

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas',
  'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White',
  'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young',
  'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores',
  'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell',
];

const ACTIVITY_TYPES = [
  'user_signup',
  'home_created',
  'home_updated',
  'home_archived',
  'subscription_upgraded',
  'subscription_downgraded',
  'subscription_cancelled',
  'invited_agent',
  'joined_business',
  'left_business',
  'profile_updated',
  'password_changed',
  'login',
  'logout',
  'task_created',
  'task_completed',
  'image_uploaded',
  'document_uploaded',
];

const BAN_REASONS = [
  'Violation of Terms of Service',
  'Spam or abusive behavior',
  'Fraudulent activity detected',
  'Multiple user reports',
  'Payment fraud',
  'Inappropriate content',
  'Harassment of other users',
  'Account security concerns',
];

function randomDate(start: Date, end: Date): string {
  const date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  return date.toISOString();
}

function generateMockUsers(count: number): AdminUser[] {
  const users: AdminUser[] = [];
  const now = new Date();
  const twoYearsAgo = new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());

  for (let i = 0; i < count; i++) {
    const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
    const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
    const name = `${firstName} ${lastName}`;
    const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}${i > 0 ? i : ''}@example.com`;

    // Tier distribution: 70% free, 20% pro, 8% agent, 2% business (already agent)
    const tierRand = Math.random();
    let tier: string;
    let isAgent = false;
    let isBusiness = false;

    if (tierRand < 0.70) {
      tier = 'free';
    } else if (tierRand < 0.90) {
      tier = 'pro';
    } else if (tierRand < 0.98) {
      tier = 'agent';
      isAgent = true;
    } else {
      tier = 'agent';
      isAgent = true;
      isBusiness = true;
    }

    // 20% of non-agent users have an agent
    const hasAgent = !isAgent && Math.random() < 0.20;

    // 5-10% banned users
    const isBanned = Math.random() < 0.075;
    const status: 'active' | 'banned' = isBanned ? 'banned' : 'active';

    const createdAt = randomDate(twoYearsAgo, now);
    const activeHomesCount = Math.floor(Math.random() * 21); // 0-20 homes

    // Last login within last 30 days for active users
    const lastLogin = status === 'active'
      ? randomDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), now)
      : randomDate(new Date(createdAt), now);

    const user: AdminUser = {
      id: `user_${i + 1}`,
      uuid: `${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`,
      name,
      email,
      picture: `https://i.pravatar.cc/150?img=${(i % 70) + 1}`,
      created_at: createdAt,
      subscription_tier: tier,
      active_homes_count: activeHomesCount,
      is_business: isBusiness,
      is_agent: isAgent,
      has_agent: hasAgent,
      is_admin: false,
      status,
      last_login: lastLogin,
    };

    if (isBanned) {
      const bannedAt = randomDate(new Date(createdAt), now);
      user.banned_at = bannedAt;
      user.ban_reason = BAN_REASONS[Math.floor(Math.random() * BAN_REASONS.length)];
      user.admin_notes = `Banned on ${new Date(bannedAt).toLocaleDateString()} for policy violations.`;
    }

    users.push(user);
  }

  return users;
}

function generateUserActivity(userId: string, createdAt: string): AdminUserActivity[] {
  const activities = [];
  const userCreatedDate = new Date(createdAt);
  const now = new Date();

  // Generate 10-30 activities
  const activityCount = Math.floor(Math.random() * 21) + 10;

  for (let i = 0; i < activityCount; i++) {
    const type = ACTIVITY_TYPES[Math.floor(Math.random() * ACTIVITY_TYPES.length)];
    const timestamp = randomDate(userCreatedDate, now);

    let description = '';
    const metadata: Record<string, string> = {};

    switch (type) {
      case 'user_signup':
        description = 'User account created';
        break;
      case 'home_created':
        description = 'Created new home';
        metadata.homeName = `Home ${Math.floor(Math.random() * 100)}`;
        break;
      case 'home_updated':
        description = 'Updated home details';
        break;
      case 'home_archived':
        description = 'Archived home';
        break;
      case 'subscription_upgraded':
        description = 'Upgraded subscription to Pro';
        metadata.tier = 'pro';
        break;
      case 'subscription_downgraded':
        description = 'Downgraded subscription to Free';
        metadata.tier = 'free';
        break;
      case 'subscription_cancelled':
        description = 'Cancelled subscription';
        break;
      case 'invited_agent':
        description = 'Invited agent to collaborate';
        break;
      case 'joined_business':
        description = 'Joined business account';
        break;
      case 'left_business':
        description = 'Left business account';
        break;
      case 'profile_updated':
        description = 'Updated profile information';
        break;
      case 'password_changed':
        description = 'Changed account password';
        break;
      case 'login':
        description = 'Logged in';
        break;
      case 'logout':
        description = 'Logged out';
        break;
      case 'task_created':
        description = 'Created maintenance task';
        break;
      case 'task_completed':
        description = 'Completed maintenance task';
        break;
      case 'image_uploaded':
        description = 'Uploaded property image';
        break;
      case 'document_uploaded':
        description = 'Uploaded document';
        break;
    }

    activities.push({
      id: `activity_${userId}_${i}`,
      type,
      description,
      timestamp,
      metadata,
    });
  }

  // Sort by timestamp descending (most recent first)
  activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return activities;
}

function generateUserHomes(count: number, userId: string): AdminUserHome[] {
  const homes = [];
  const addresses = [
    '123 Main St, Springfield, IL',
    '456 Oak Ave, Portland, OR',
    '789 Elm Drive, Austin, TX',
    '321 Maple Ln, Denver, CO',
    '654 Pine St, Seattle, WA',
    '987 Cedar Rd, Miami, FL',
    '147 Birch Way, Boston, MA',
    '258 Willow Ct, Phoenix, AZ',
  ];

  for (let i = 0; i < count; i++) {
    const createdAt = randomDate(new Date(2022, 0, 1), new Date());
    const status: 'active' | 'archived' = Math.random() > 0.2 ? 'active' : 'archived';
    homes.push({
      id: `home_${userId}_${i}`,
      name: `My Home ${i + 1}`,
      address: addresses[Math.floor(Math.random() * addresses.length)],
      status,
      created_at: createdAt,
    });
  }

  return homes;
}

// Generate 100 mock users once
const MOCK_USERS = generateMockUsers(100);

/**
 * useAdminUsers - Hook for user management operations
 *
 * Provides:
 * - User list with pagination
 * - Search and filtering
 * - Ban/unban users
 * - Delete users
 * - User detail fetching
 */
export function useAdminUsers() {
  const [allUsers] = useState<AdminUser[]>(MOCK_USERS);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<UserFilters>({});
  const [pagination, setPagination] = useState<Pick<AdminPagination, 'page' | 'limit'>>({
    page: 1,
    limit: 50,
  });
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Filter and search users
  const filteredUsers = useMemo(() => {
    let result = [...allUsers];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(
        (user) =>
          user.name.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower)
      );
    }

    // Tier filter
    if (filters.tier) {
      result = result.filter((user) => user.subscription_tier === filters.tier);
    }

    // Status filter
    if (filters.status) {
      result = result.filter((user) => user.status === filters.status);
    }

    // Type filter
    if (filters.type) {
      switch (filters.type) {
        case 'business':
          result = result.filter((user) => user.is_business);
          break;
        case 'agent':
          result = result.filter((user) => user.is_agent && !user.is_business);
          break;
        case 'has_agent':
          result = result.filter((user) => user.has_agent);
          break;
        case 'regular':
          result = result.filter((user) => !user.is_business && !user.is_agent && !user.has_agent);
          break;
      }
    }

    // Date range filter
    if (filters.created_from) {
      result = result.filter(
        (user) => new Date(user.created_at) >= new Date(filters.created_from!)
      );
    }
    if (filters.created_to) {
      result = result.filter(
        (user) => new Date(user.created_at) <= new Date(filters.created_to!)
      );
    }

    // Sort by created_at descending (newest first)
    result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    return result;
  }, [allUsers, filters]);

  // Paginated users
  const paginatedUsers = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.limit;
    const endIndex = startIndex + pagination.limit;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, pagination]);

  const totalUsers = filteredUsers.length;
  const totalPages = Math.ceil(totalUsers / pagination.limit);

  // Get user detail
  const getUserDetail = useCallback((userId: string): AdminUserDetail | null => {
    const user = allUsers.find((u) => u.id === userId);
    if (!user) return null;

    const homes = generateUserHomes(user.active_homes_count, userId);
    const activity = generateUserActivity(userId, user.created_at);

    // Generate relationships
    const relationships: AdminUserRelationships = {};
    if (user.is_business) {
      relationships.business = {
        id: `business_${userId}`,
        name: `${user.name.split(' ')[1]} Realty`,
        role: 'Owner',
      };
      relationships.clients = Array.from({ length: Math.floor(Math.random() * 10) + 5 }, (_, i) => ({
        name: `Client ${i + 1}`,
        email: `client${i + 1}@example.com`,
      }));
    } else if (user.is_agent) {
      relationships.business = {
        id: `business_${Math.floor(Math.random() * 20)}`,
        name: 'Premium Realty Group',
        role: 'Agent',
      };
      relationships.clients = Array.from({ length: Math.floor(Math.random() * 15) + 3 }, (_, i) => ({
        name: `Client ${i + 1}`,
        email: `client${i + 1}@example.com`,
      }));
    } else if (user.has_agent) {
      relationships.agent = {
        name: 'John Agent',
        email: 'john.agent@realty.com',
        business: 'Premium Realty Group',
      };
    }

    return {
      ...user,
      auth_type: 'google',
      homes,
      activity,
      relationships,
      subscription: {
        tier: user.subscription_tier,
        status: user.status === 'active' ? 'active' : 'cancelled',
        paddle_subscription_id: user.subscription_tier !== 'free' ? `sub_${userId}` : undefined,
        current_period_start: user.subscription_tier !== 'free' ? new Date().toISOString() : undefined,
        current_period_end: user.subscription_tier !== 'free'
          ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          : undefined,
      },
    };
  }, [allUsers]);

  // Search users
  const searchUsers = useCallback((query: string) => {
    setFilters((prev) => ({ ...prev, search: query }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Filter users
  const filterUsers = useCallback((newFilters: Partial<UserFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({});
    setPagination({ page: 1, limit: 50 });
  }, []);

  // Ban user
  const banUser = useCallback(
    async (userId: string, reason: string) => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Update user in mock data
        const userIndex = allUsers.findIndex((u) => u.id === userId);
        if (userIndex !== -1) {
          allUsers[userIndex] = {
            ...allUsers[userIndex],
            status: 'banned',
            banned_at: new Date().toISOString(),
            ban_reason: reason,
            admin_notes: `Banned on ${new Date().toLocaleDateString()} - ${reason}`,
          };
        }

        setIsLoading(false);
        return { success: true };
      } catch {
        setIsLoading(false);
        return { success: false, error: 'Failed to ban user' };
      }
    },
    [allUsers]
  );

  // Unban user
  const unbanUser = useCallback(
    async (userId: string) => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Update user in mock data
        const userIndex = allUsers.findIndex((u) => u.id === userId);
        if (userIndex !== -1) {
          const user = { ...allUsers[userIndex] };
          delete user.banned_at;
          delete user.ban_reason;
          user.status = 'active';
          user.admin_notes = `Unbanned on ${new Date().toLocaleDateString()}`;
          allUsers[userIndex] = user;
        }

        setIsLoading(false);
        return { success: true };
      } catch {
        setIsLoading(false);
        return { success: false, error: 'Failed to unban user' };
      }
    },
    [allUsers]
  );

  // Delete user
  const deleteUser = useCallback(
    async (userId: string) => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Remove user from mock data
        const userIndex = allUsers.findIndex((u) => u.id === userId);
        if (userIndex !== -1) {
          allUsers.splice(userIndex, 1);
        }

        setIsLoading(false);
        return { success: true };
      } catch {
        setIsLoading(false);
        return { success: false, error: 'Failed to delete user' };
      }
    },
    [allUsers]
  );

  return {
    users: paginatedUsers,
    totalUsers,
    isLoading,
    error: null,
    pagination: {
      ...pagination,
      total: totalUsers,
      total_pages: totalPages,
    },
    filters,
    searchUsers,
    filterUsers,
    clearFilters,
    setPagination,
    banUser,
    unbanUser,
    deleteUser,
    getUserDetail,
    selectedUserId,
    setSelectedUserId,
  };
}
